import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiGet, TENANT_TOKEN_KEY } from '../../../api/client';
import { conversationIdFromDeepLink } from './conversationIdFromDeepLink';

export type OperatorNotificationBrief = {
  id: string;
  type: string;
  status: string;
  title: string;
  body: string | null;
  deepLinkPath: string;
  payload?: unknown;
  backgroundColor: string;
  dismissedAt: string | null;
  updatedAt: string;
};

export function useOpenOperatorNotifications(enabled: boolean, pollMs = 12_000) {
  const [items, setItems] = useState<OperatorNotificationBrief[]>([]);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!enabled) {
      setItems([]);
      return;
    }
    const token = localStorage.getItem(TENANT_TOKEN_KEY);
    if (!token) return;
    setLoading(true);
    try {
      const data = await apiGet<{ items: OperatorNotificationBrief[] }>(
        '/api/console/notifications?take=20',
        token,
        { showErrorGrowl: false }
      );
      setItems(data.items);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    void refresh();
    if (!enabled) return;
    const id = window.setInterval(() => void refresh(), pollMs);
    return () => window.clearInterval(id);
  }, [refresh, enabled, pollMs]);

  useEffect(() => {
    const onUpdated = () => void refresh();
    window.addEventListener('va-operator-notifications-updated', onUpdated);
    return () => window.removeEventListener('va-operator-notifications-updated', onUpdated);
  }, [refresh]);

  const openItems = useMemo(
    () => items.filter((n) => n.status === 'open' && !n.dismissedAt),
    [items]
  );

  const alertColorByConversationId = useMemo(() => {
    const map = new Map<string, string>();
    for (const n of openItems) {
      const cid = conversationIdFromDeepLink(n.deepLinkPath);
      if (!cid || map.has(cid)) continue;
      if (n.backgroundColor) map.set(cid, n.backgroundColor);
    }
    return map;
  }, [openItems]);

  return { openItems, alertColorByConversationId, refresh, loading };
}
